/**
 * Support ticket schemas — F-013 (form di contatto in-app).
 *
 * Zod schemi per le 2 route del modulo supporto:
 *  - `supportTicketUploadUrlRequestSchema` → POST /api/support/ticket/upload-url
 *  - `createSupportTicketRequestSchema`    → POST /api/support/ticket
 *
 * Tutti gli schemi body sono `.strict()` per bloccare chiavi extra
 * (anti-tampering), coerente col pattern in `pratica-submit.ts`.
 */
import { z } from 'zod';
/** Categorie ticket ammesse (UI Behavior F-013). Nuove categorie richiedono modifica di catalogo. */
export const SUPPORT_TICKET_CATEGORIES = [
    'bug',
    'domanda',
    'pratica',
    'commerciale',
    'altro',
];
export const supportTicketCategoriaSchema = z.enum(SUPPORT_TICKET_CATEGORIES);
export const SUPPORT_TICKET_OGGETTO_MAX = 120;
export const SUPPORT_TICKET_MESSAGGIO_MAX = 2000;
/** Rate limit BR-3-equivalente (Technical Notes F-013): max ticket/ora per account. */
export const SUPPORT_TICKET_MAX_PER_HOUR = 5;
export const SUPPORT_TICKET_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const SUPPORT_TICKET_ATTACHMENT_CONTENT_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/webp',
];
export const supportTicketAttachmentContentTypeSchema = z.enum(SUPPORT_TICKET_ATTACHMENT_CONTENT_TYPES);
/* -------------------------------------------------------------------------- */
/* POST /api/support/ticket/upload-url                                        */
/* -------------------------------------------------------------------------- */
/**
 * Il ticket non esiste ancora al momento del presign (l'utente allega il file
 * prima di inviare il form): la key S3 usa un `attachmentId` generato
 * server-side al posto del `ticketId` (che non esiste ancora). Vedi
 * `support-ticket-service.ts` per il dettaglio del path.
 */
export const supportTicketUploadUrlRequestSchema = z
    .object({
    fileName: z.string().trim().min(1).max(255),
    contentType: supportTicketAttachmentContentTypeSchema,
})
    .strict();
export const supportTicketUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    key: z.string(),
    expiresAt: z.string(),
});
/* -------------------------------------------------------------------------- */
/* POST /api/support/ticket                                                   */
/* -------------------------------------------------------------------------- */
export const createSupportTicketRequestSchema = z
    .object({
    categoria: supportTicketCategoriaSchema,
    oggetto: z
        .string()
        .trim()
        .min(1, { message: 'Oggetto obbligatorio' })
        .max(SUPPORT_TICKET_OGGETTO_MAX, { message: `Oggetto max ${SUPPORT_TICKET_OGGETTO_MAX} caratteri` }),
    messaggio: z
        .string()
        .trim()
        .min(1, { message: 'Messaggio obbligatorio' })
        .max(SUPPORT_TICKET_MESSAGGIO_MAX, {
        message: `Messaggio max ${SUPPORT_TICKET_MESSAGGIO_MAX} caratteri`,
    }),
    /** Riferimento pratica auto-popolato se il ticket è aperto da una detail view. */
    praticaId: z.string().trim().min(1).optional(),
    /** Key S3 restituita da `POST /api/support/ticket/upload-url` dopo l'upload. */
    allegatoS3Key: z.string().trim().min(1).optional(),
})
    .strict();
export const supportTicketResponseSchema = z.object({
    id: z.string(),
    referenceNumber: z.string(),
    categoria: supportTicketCategoriaSchema,
    oggetto: z.string(),
    praticaId: z.string().nullable(),
    createdAt: z.string(),
});
//# sourceMappingURL=support.js.map